import React from 'react';
import { Link } from 'react-router-dom';
import { openCookiePreferences } from './CookieConsent';
import { useTheme } from '../contexts/ThemeContext';

type NavLink = { to?: string; label: string; onClick?: () => void };

const PRIMARY: NavLink[] = [
  { to: '/work', label: 'work' },
  { to: '/articles', label: 'articles' },
  { to: '/about', label: 'about' },
  { to: '/contact', label: 'contact' },
];

const MORE: NavLink[] = [
  { to: '/privacy', label: 'privacy' },
  { label: 'cookies', onClick: openCookiePreferences },
  { to: '/admin/login', label: 'login' },
];

const linkClass =
  'uppercase border-b border-transparent hover:text-text hover:border-accent-alt-hover transition-colors';

const NavItem: React.FC<{ item: NavLink }> = ({ item }) => {
  if (item.onClick) {
    return (
      <button type="button" onClick={item.onClick} className={linkClass}>
        {item.label}
      </button>
    );
  }
  return (
    <Link to={item.to!} className={linkClass}>
      {item.label}
    </Link>
  );
};

const Kicker: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-accent text-[10px] uppercase tracking-[1px] kicker-line mb-s2">
    {children}
  </div>
);

const Footer: React.FC = () => {
  const { team, mode } = useTheme();
  const year = new Date().getFullYear();
  const build = import.meta.env.VITE_BUILD_DATE || 'dev';

  return (
    <footer className="border-t border-border bg-bg">
      <div className="max-w-content mx-auto px-s6 md:px-s8 py-s6 font-mono text-[11px] text-text-dim tracking-[0.4px]">
        {/* Desktop: single row, justify-between */}
        <div className="hidden md:flex items-center justify-between gap-s4">
          <span>
            &copy; {year} hi! i'm joe &middot; built in charlotte &middot; last deploy {build}
          </span>
          <nav className="flex flex-wrap items-center gap-x-s4 gap-y-s2">
            {[...PRIMARY, ...MORE].map(item => (
              <NavItem key={item.label} item={item} />
            ))}
          </nav>
          <span className="uppercase">
            {team} &middot; {mode}
          </span>
        </div>

        {/* Mobile: grouped, stacked */}
        <div className="md:hidden flex flex-col gap-s5">
          <div>
            <Kicker>navigate</Kicker>
            <nav className="flex flex-wrap gap-x-s4 gap-y-s2">
              {PRIMARY.map(item => (
                <NavItem key={item.label} item={item} />
              ))}
            </nav>
          </div>

          <div>
            <Kicker>more</Kicker>
            <nav className="flex flex-wrap gap-x-s4 gap-y-s2">
              {MORE.map(item => (
                <NavItem key={item.label} item={item} />
              ))}
            </nav>
          </div>

          <div className="pt-s4 border-t border-border">
            <span className="whitespace-nowrap">
              &copy; {year} hi! i'm joe &middot; built in charlotte
            </span>
          </div>

          <div>
            <span className="text-text-dim">theme selected</span>{' '}
            <span className="text-text-muted">=</span>{' '}
            <span className="uppercase text-text">
              {team} &middot; {mode}
            </span>
            <span className="text-text-dim ml-s3">&middot; deploy {build}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
